import React, { useState } from "react";
import { useMembership } from "@/components/membership/MembershipContext";
import UpgradeModal from "@/components/membership/UpgradeModal";

const TIER_COLORS = {
  free: "#60B8E8",
  professional: "#7AC860",
  executive: "#F5C842",
  enterprise: "#E8A020",
};

/**
 * LockedFeature wraps content that requires a minimum tier.
 * - If the caller's tier is sufficient, renders children unchanged.
 * - Otherwise renders a sober "LOCKED" gold lock overlay with an upgrade CTA.
 */
export default function LockedFeature({
  featureKey,
  children,
  size = "md",
  inline = false,
  className = "",
  showBadgeWhenAllowed = false,
}) {
  const { allowed, requiredTier } = useMembership();
  const required = requiredTier(featureKey);
  const ok = allowed(featureKey);
  const [open, setOpen] = useState(false);

  if (ok) {
    return showBadgeWhenAllowed ? (
      <span className={`locked-wrap ${className}`}>
        {children}
        <TierBadge tier={required} unlocked />
      </span>
    ) : children;
  }

  const color = TIER_COLORS[required] || "#F5C842";
  return (
    <span className={`locked-feature locked-feature--${size} ${inline ? "is-inline" : ""} ${className}`} data-testid={`locked-${featureKey}`}>
      <span className="locked-feature__shroud" aria-hidden="true">{children}</span>
      <button
        type="button"
        className="locked-feature__overlay"
        onClick={() => setOpen(true)}
        style={{ borderColor: color, color }}
        data-testid={`locked-cta-${featureKey}`}
        aria-label={`Locked: requires ${required} tier`}
      >
        <span className="locked-feature__lock" aria-hidden="true">❖</span>
        <span className="locked-feature__txt">
          <span className="locked-feature__title">LOCKED</span>
          <span className="locked-feature__sub">{required.toUpperCase()} tier required</span>
        </span>
      </button>
      {open && <UpgradeModal featureKey={featureKey} requiredTier={required} onClose={() => setOpen(false)} />}
    </span>
  );
}

export function TierBadge({ tier, unlocked = false, className = "" }) {
  const color = TIER_COLORS[tier] || "#F5C842";
  return (
    <span
      className={`tier-badge ${unlocked ? "is-unlocked" : ""} ${className}`}
      style={{ borderColor: color, color }}
      data-testid={`tier-badge-${tier}`}
    >
      {(tier || "").toUpperCase()}
    </span>
  );
}
